<template>
  <RevDrawer
    :back-button-label="i18n(translations.backButtonAriaLabel)"
    :has-back-button="!shouldDisplayFiltersList"
    :name="MODAL_NAMES.MOBILE_FILTER"
    :title="modalTitle"
    @back="displayFiltersList"
  >
    <template #body>
      <div class="flex h-full flex-col justify-between">
        <div>
          <RevList v-if="shouldDisplayFiltersList">
            <template v-for="filter in FILTERS_LIST" :key="filter.name">
              <RevListItemInteractive @click="displayFilterPage(filter.name)">
                <template #label>{{ i18n(filter.label) }}</template>
                <template #description>
                  <div class="truncate pr-6">
                    {{ displaySelectedFilters(filter.name) }}
                  </div>
                </template>
                <template #suffix>
                  <IconChevronRight />
                </template>
              </RevListItemInteractive>
            </template>
          </RevList>

          <template v-if="!shouldDisplayFiltersList">
            <RevList
              v-if="shouldDisplayFilterPage(FILTERS_LIST.status.name)"
              class="mt-7"
            >
              <template
                v-for="status in statusFilterOption"
                :key="status.value"
              >
                <RevListItemInteractive class="last:first:!border-0">
                  <template #prefix>
                    <RevCheckbox
                      :id="status.value"
                      v-model="filtersValues.status"
                      :label="status.label"
                      :value="status.value"
                    >
                      <template #label>
                        {{ status.label }}
                      </template>
                    </RevCheckbox>
                  </template>
                </RevListItemInteractive>
              </template>
            </RevList>

            <RevList
              v-if="shouldDisplayFilterPage(FILTERS_LIST.taskTypes.name)"
              class="mt-7"
            >
              <template v-for="task in useTypeOptions()" :key="task.value">
                <RevListItemInteractive>
                  <template #prefix>
                    <RevCheckbox
                      :id="task.value"
                      v-model="filtersValues.taskTypes"
                      :label="task.label"
                      :value="task.value"
                    >
                      <template #label>
                        {{ task.label }}
                      </template>
                    </RevCheckbox>
                  </template>
                </RevListItemInteractive>
              </template>
            </RevList>

            <RevInputText
              v-if="shouldDisplayFilterPage(FILTERS_LIST.orderId.name)"
              id="orderId"
              v-model="filtersValues.orderId"
              class="mt-7"
              :has-cleaning-icon="false"
              :label="i18n(translations.orderId)"
            />
            <RevInputText
              v-if="
                shouldDisplayFilterPage(FILTERS_LIST.customerFirstName.name)
              "
              id="customerFirstName"
              v-model="filtersValues.customerFirstName"
              class="mt-7"
              :has-cleaning-icon="false"
              :label="i18n(translations.customerFirstName)"
            />
            <RevInputText
              v-if="shouldDisplayFilterPage(FILTERS_LIST.customerLastName.name)"
              id="customerLastName"
              v-model="filtersValues.customerLastName"
              class="mt-7"
              :has-cleaning-icon="false"
              :label="i18n(translations.customerLastName)"
            />

            <RevList
              v-if="shouldDisplayFilterPage(FILTERS_LIST.markets.name)"
              class="mt-7"
            >
              <template v-for="market in marketOptions" :key="market.value">
                <RevListItemInteractive>
                  <template #prefix>
                    <RevCheckbox
                      :id="market.value"
                      v-model="filtersValues.markets"
                      :label="market.label"
                      :value="market.value"
                    >
                      <template #label>
                        {{ market.label }}
                      </template>
                    </RevCheckbox>
                  </template>
                </RevListItemInteractive>
              </template>
            </RevList>

            <RevInputText
              v-if="shouldDisplayFilterPage(FILTERS_LIST.sku.name)"
              id="sku"
              v-model="filtersValues.sku"
              class="mt-7"
              :has-cleaning-icon="false"
              :label="i18n(translations.sku)"
            />
          </template>
        </div>
      </div>
    </template>

    <template #footer="{ close }">
      <RevButton
        class="mb-8"
        form="filtersForm"
        full-width="always"
        variant="primary"
        @click="handleSubmit(close)"
      >
        {{ i18n(translations.applyFilters) }}
      </RevButton>

      <RevButton
        full-width="always"
        variant="secondary"
        @click="resetFilters(close)"
      >
        {{ i18n(translations.reset) }}
      </RevButton>
    </template>

    <template #trigger="{ open }">
      <RevStickyBar class="md:hidden">
        <div class="flex items-center justify-center">
          <RevButton variant="secondary" @click="open">
            <div class="flex">
              <span>{{ i18n(translations.modalButtonLabel) }}</span>
              <RevTag
                v-if="hasCurrentFilters"
                :label="`${currentFiltersCount}`"
                variant="secondary"
              />
              <IconEqualizer class="ml-12" />
            </div>
          </RevButton>
        </div>
      </RevStickyBar>
    </template>
  </RevDrawer>
</template>

<script setup lang="ts">
import { computed, reactive, ref } from 'vue'

import type { I18nDefinition } from '@backmarket/nuxt-module-i18n/types'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTracking } from '@backmarket/nuxt-module-tracking/useTracking'
import { isEmpty } from '@backmarket/utils/object/isEmpty'
import { RevButton } from '@ds/components/Button'
import { RevCheckbox } from '@ds/components/Checkbox'
import { RevDrawer } from '@ds/components/Drawer'
import { RevInputText } from '@ds/components/InputText'
import { RevList } from '@ds/components/List'
import { RevListItemInteractive } from '@ds/components/ListItemInteractive'
import { RevStickyBar } from '@ds/components/StickyBar'
import { RevTag } from '@ds/components/Tag'
import { IconChevronRight } from '@ds/icons/IconChevronRight'
import { IconEqualizer } from '@ds/icons/IconEqualizer'

import type {
  Filter,
  FilterOptionItem,
} from '~/api/seller-after-sales/types/requests.types'
import { MODAL_NAMES } from '~/scopes/customer-care/modalNames'
import { useMarketOptions } from '~/scopes/customer-care/pages/customer-care/composables/useMarketOptions'
import { useTypeOptions } from '~/scopes/customer-care/pages/customer-care/composables/useTypeOptions'

import { EMPTY_FILTERS_VALUES, FILTERS_LIST } from '../Filters.constants'
import translations from '../Filters.translations'

const props = defineProps<{
  pageType: string
  statusFilterOption: Array<FilterOptionItem>
  initialValues: Filter
}>()

const i18n = useI18n()
const filterPageToDisplay = ref('')
const showFiltersList = ref(true)
const filtersValues: Filter = reactive({
  ...props.initialValues,
})
const marketOptions = useMarketOptions()
const emit = defineEmits(['apply-filters'])
const tracking = useTracking()

const currentFiltersCount = computed(() => {
  return Object.keys(EMPTY_FILTERS_VALUES).filter((key) => {
    return !isEmpty(filtersValues[key as keyof Filter])
  }).length
})

const hasCurrentFilters = computed(() => currentFiltersCount.value > 0)

const shouldDisplayFiltersList = computed(() => {
  return showFiltersList.value || !filterPageToDisplay.value
})

const modalTitle = computed(() => {
  if (shouldDisplayFiltersList.value) {
    return i18n(translations.modalTitle)
  }

  const filterPageTitle = Object.values(FILTERS_LIST).find(
    (filterField) => filterField.name === filterPageToDisplay.value,
  )

  return i18n(filterPageTitle?.label as I18nDefinition)
})

function displayFiltersList() {
  filterPageToDisplay.value = ''
  showFiltersList.value = true
}

function handleSubmit(closeModal: () => void) {
  tracking.trackClick({
    name: 'filters_submited',
    page_type: props.pageType,
    status_filter: filtersValues.status,
    task_type_filter: filtersValues.taskTypes,
    order_id_filter: filtersValues.orderId,
    customer_fist_name_filter: filtersValues.customerFirstName,
    customer_last_name_filter: filtersValues.customerLastName,
    market_filter: filtersValues.markets,
    sku_filter: filtersValues.sku,
  })
  emit('apply-filters', filtersValues)
  closeModal()
}

function resetFilters(closeModal: () => void) {
  Object.assign(filtersValues, EMPTY_FILTERS_VALUES)
  emit('apply-filters', filtersValues)
  displayFiltersList()
  closeModal()
}

function displayFilterPage(pageName: string) {
  filterPageToDisplay.value = pageName
  showFiltersList.value = false
}

function shouldDisplayFilterPage(pageName: string) {
  return filterPageToDisplay.value === pageName
}

function getLabelsFromOptions(
  filterName: 'status' | 'taskTypes' | 'markets',
  options: Array<FilterOptionItem>,
) {
  // TODO-SAX: Try to replace findIndex by find
  const result = filtersValues[filterName].map((selectedFilter: string) => {
    const indexOfSelectedFilterInOptions = options.findIndex(
      (element) => element.value === selectedFilter,
    )

    return options[indexOfSelectedFilterInOptions].label
  })

  return result.join(', ')
}

function displaySelectedFilters(filterName: string) {
  const multiSelectEmptyLabel = {
    [FILTERS_LIST.status.name]: i18n(translations.statusFilterEmptyLabel),
    [FILTERS_LIST.taskTypes.name]: i18n(translations.taskFilterEmptyLabel),
    [FILTERS_LIST.markets.name]: i18n(translations.marketFilterEmptyLabel),
  } as Record<string, string>

  if (isEmpty(filtersValues[filterName as keyof Filter])) {
    return multiSelectEmptyLabel[filterName]
  }

  if (filterName === FILTERS_LIST.status.name) {
    return getLabelsFromOptions('status', props.statusFilterOption)
  }

  if (filterName === FILTERS_LIST.taskTypes.name) {
    return getLabelsFromOptions('taskTypes', useTypeOptions())
  }

  if (filterName === FILTERS_LIST.markets.name) {
    const result = filtersValues.markets.map((country: string) =>
      i18n.country(country),
    )

    return result.join(', ')
  }

  return filtersValues[filterName as keyof Filter]
}
</script>
